import { GoogleReCaptchaProvider } from 'lib/react-google-recaptcha-v3';
import type { GivingDetail } from 'models';
import { useCallback, useState } from 'react';
import { Modal, message } from 'ui';
import { useCheckoutTransactionMutation, useCreateTransactionMutation } from 'features/transactions';
import type { GivingCreateFormValues } from '../components/giving-create-form';
import { GivingCreateForm } from '../components/giving-create-form';
import { GivingImageWithTitle } from '../components/giving-image-with-title';

import { useGivingCreate } from '../hooks';
import { GivingCreateSummary } from './giving-create-summary';

enum GivingStepsEnum {
  FORM = 0,
  SUMMARY = 1,
}

export type GivingCreateModalType = 'default' | 'giveByCheck';

type P = {
  giving: GivingDetail;
  isOpened: boolean;
  onClose: () => void;
  paymentRedirectUrl: string;
  type?: GivingCreateModalType;
};

const GivingCreateModal = ({ giving, isOpened, onClose, paymentRedirectUrl, type = 'default' }: P) => {
  const [step, setStep] = useState(GivingStepsEnum.FORM);
  const [formValues, setFormValues] = useState<GivingCreateFormValues>();

  const { isPending: isLoadingCreateTransaction, mutate: createTransaction } = useCreateTransactionMutation();
  const { isPending: isLoadingCheckoutTransaction, mutate: checkoutTransaction } = useCheckoutTransactionMutation();

  const { createRequestData } = useGivingCreate();

  const handleCancel = useCallback(() => {
    onClose();
  }, [onClose]);

  const onContinue = useCallback((values: GivingCreateFormValues) => {
    setFormValues(values);
    setStep(GivingStepsEnum.SUMMARY);
  }, []);

  const onBack = useCallback(() => {
    setStep(GivingStepsEnum.FORM);
  }, []);

  const onFinish = useCallback(
    (submittedFormValues: GivingCreateFormValues, googleRecaptchaKey: string) => {
      if (type === 'giveByCheck') {
        // TODO call give be check endpoint
        message.success('MOCKED DATA - Your give has been submitted.');
        return;
      }
      createTransaction(createRequestData({ formValues: submittedFormValues, giving, googleRecaptchaKey }), {
        onSuccess({ id }) {
          checkoutTransaction(
            { id, pathname: paymentRedirectUrl },
            {
              onSuccess(paymentData) {
                window.location.href = paymentData.checkoutUrl;
              },
              // TODO: we'll likely need a retry mechanism for failed checkouts without creating new transactions.
            },
          );
        },
      });
    },
    [type, createTransaction, createRequestData, giving, checkoutTransaction, paymentRedirectUrl],
  );

  return (
    <Modal
      isOpened={isOpened}
      maskClosable={false}
      onCancel={handleCancel}
      title={step === GivingStepsEnum.FORM ? 'My giving options' : 'My giving summary'}
      width="medium"
    >
      <Modal.HugContent>
        <GoogleReCaptchaProvider>
          <GivingImageWithTitle giving={giving} titleLevel={3} />

          {step === GivingStepsEnum.FORM ? (
            <GivingCreateForm
              giving={giving}
              initialValues={formValues}
              onCancel={handleCancel}
              onSuccess={onContinue}
              type={type}
            />
          ) : null}

          {step === GivingStepsEnum.SUMMARY && formValues ? (
            <GivingCreateSummary
              formValues={formValues}
              // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- TODO: disabled by automatic tool, need to check if it could be fixed or stay ignored in some rare cases
              isLoading={isLoadingCreateTransaction ?? isLoadingCheckoutTransaction}
              onCancel={onBack}
              onContinue={onFinish}
              type={type}
            />
          ) : null}
        </GoogleReCaptchaProvider>
      </Modal.HugContent>
    </Modal>
  );
};

export default GivingCreateModal;
